import { LoadingSpinner } from "components/LoadingSpinner";
import { centsToDollars, parsePrice } from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dispatch } from "react";
import { AnyAction } from "redux";
import {
  toggleDisplayPatientResponsibility,
  IPriceBreakdownRows,
} from "store/slices/serviceDetails";
import styled from "styled-components";

import {
  buildCptCodeWithRvu,
  buildSimpleFirstColumnTemplate,
} from "../../builders";
import { summariseBreakdown } from "../Common/breakdown";
import { BreakdownHeader } from "../Common/BreakdownHeader";
import { StyledBreakdownPanel } from "../Common/styled";

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
`;

function amountTemplate(row: { amount: number }) {
  return (
    <span className="font-semibold" style={{ color: "var(--brand-black)" }}>
      {parsePrice(centsToDollars(row.amount))}
    </span>
  );
}

export function PatientResponsibilityDetails({
  displayPatientResponsibility,
  priceBreakdownRows,
  dispatch,
  priceExecuting,
}: {
  displayPatientResponsibility: boolean;
  priceBreakdownRows: IPriceBreakdownRows;
  dispatch: Dispatch<AnyAction>;
  priceExecuting: boolean;
}) {
  const { count, totalInCents } = summariseBreakdown(
    priceBreakdownRows.patientResponsibility
  );

  return (
    <StyledBreakdownPanel
      data-testid="gfeServiceDetails_PatientResponsibility"
      aria-disabled={priceBreakdownRows.patientResponsibility.length > 0}
    >
      <BreakdownHeader
        title="Patient Balance"
        count={count}
        totalInCents={totalInCents}
        noun="service"
        expanded={displayPatientResponsibility}
        onToggle={() => dispatch(toggleDisplayPatientResponsibility())}
      />

      {(() => {
        if (!displayPatientResponsibility) {
          return null;
        }

        if (!priceExecuting) {
          return (
            <div className="breakdown-body">
              <DataTable
                value={priceBreakdownRows.patientResponsibility}
                tableStyle={{
                  minWidth: "100%",
                }}
                responsiveLayout="scroll"
                emptyMessage="Nothing owed by patient."
              >
                <Column
                  field="providerName"
                  style={{ width: "28%" }}
                  body={(row) => buildSimpleFirstColumnTemplate(row)}
                />
                <Column
                  field="internalId"
                  style={{ width: "26%", color: "var(--brand-gray-600)" }}
                  body={(row) => buildCptCodeWithRvu(row)}
                />
                <Column
                  field="amount"
                  style={{ width: "19%" }}
                  body={(row) => amountTemplate(row)}
                />
                <Column
                  field="specialty"
                  style={{ width: "27%", color: "var(--brand-gray-600)" }}
                  body={(row) => row.specialty}
                />
              </DataTable>
            </div>
          );
        }

        return (
          <div className="breakdown-body">
            <LoadingContainer>
              <LoadingSpinner customStyle={{ width: "60px", height: "60px" }} />
              <h3>Calculating Patient Balance</h3>
            </LoadingContainer>
          </div>
        );
      })()}
    </StyledBreakdownPanel>
  );
}
